import React from 'react';
import TextInput from './TextInput';
import SelectInput from './SelectInput';
import CheckboxInput from './CheckboxInput';

export const EMPTY_ADDRESS = {
  street: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  country: 'USA',
};

export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI',
  'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN',
  'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH',
  'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA',
  'WV', 'WI', 'WY', 'AS', 'GU', 'MP', 'PR', 'VI',
];

/**
 * Schwab and Fidelity reject PO Boxes for legal/home and trusted-contact
 * addresses, so we catch the common spellings before the client submits.
 */
export const PO_BOX_RE = /\b(p\.?\s*o\.?\s*box|post\s+office\s+box|po\s*bx)\b/i;

export function addressHasData(a) {
  if (!a || typeof a !== 'object') return false;
  return ['street', 'street2', 'city', 'state', 'zip']
    .some((k) => String(a[k] || '').trim());
}

export function addressIsComplete(a) {
  return ['street', 'city', 'state', 'zip']
    .every((k) => String(a?.[k] || '').trim());
}

export function addressHasPoBox(a) {
  return PO_BOX_RE.test(String(a?.street || ''))
    || PO_BOX_RE.test(String(a?.street2 || ''));
}

const ADDRESS_KEYS = ['street', 'street2', 'city', 'state', 'zip', 'country'];

/** Field-by-field equality, used to derive the "same address" checkbox state. */
export function addressesMatch(a, b) {
  if (!a || !b) return false;
  return ADDRESS_KEYS.every(
    (k) => String(a[k] || '').trim() === String(b[k] || '').trim(),
  );
}

/** Render an address on a single line, for the review step. */
export function formatAddress(a) {
  if (!addressHasData(a)) return '';
  const line1 = [a.street, a.street2].filter(Boolean).join(', ');
  const line2 = [a.city, [a.state, a.zip].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  const country = a.country && a.country !== 'USA' ? a.country : '';
  return [line1, line2, country].filter(Boolean).join(', ');
}

/**
 * Structured address capture (street / street 2 / city / state / ZIP).
 *
 * @param {string} idPrefix        - Unique prefix for the field ids.
 * @param {string} [legend]        - Optional heading above the fields.
 * @param {object} value           - Address object; see EMPTY_ADDRESS.
 * @param {function} onChange      - Receives the whole updated address.
 * @param {boolean} [required]
 * @param {boolean} [disallowPoBox] - Warn inline when a PO Box is entered.
 * @param {object} [sameAsAddress]  - When supplied and non-empty, show a
 *   checkbox that copies this address in. Ticked state is derived by
 *   comparison rather than stored, so nothing extra has to be persisted.
 * @param {string} [sameAsLabel]
 */
export default function AddressFields({
  idPrefix,
  legend,
  value,
  onChange,
  required = false,
  disallowPoBox = false,
  sameAsAddress,
  sameAsLabel = 'Same mailing address',
}) {
  const address = value || EMPTY_ADDRESS;
  const update = (field) => (val) => onChange({ ...address, [field]: val });
  const poBox = disallowPoBox && addressHasPoBox(address);

  // Only offer the shortcut once there is something worth copying.
  const canCopy = addressHasData(sameAsAddress);
  const copied = canCopy && addressesMatch(address, sameAsAddress);
  const toggleCopy = (checked) => onChange(
    checked ? { ...EMPTY_ADDRESS, ...sameAsAddress } : { ...EMPTY_ADDRESS },
  );

  return (
    <div>
      {legend && (
        <div className="cashflow-subsection-title mb-2">
          {legend}
          {required && <span className="text-danger"> *</span>}
        </div>
      )}
      {canCopy && (
        <div className="mb-2">
          <CheckboxInput
            id={`${idPrefix}-same-as`}
            label={sameAsLabel}
            checked={copied}
            onChange={toggleCopy}
          />
        </div>
      )}
      <div className="row g-2">
        <TextInput
          id={`${idPrefix}-street`}
          label="Street Address"
          col="col-12"
          required={required}
          disabled={copied}
          value={address.street}
          onChange={update('street')}
          autoComplete="off"
        />
        {poBox && (
          <div className="col-12">
            <div className="alert alert-warning py-1 px-2 small mb-0">
              <i className="bi bi-exclamation-triangle me-1" />
              Schwab and Fidelity cannot accept a PO Box for this address.
              Please enter a physical street address.
            </div>
          </div>
        )}
        <TextInput
          id={`${idPrefix}-street2`}
          label="Apt / Suite / Unit"
          col="col-12"
          disabled={copied}
          value={address.street2}
          onChange={update('street2')}
          autoComplete="off"
        />
        <TextInput
          id={`${idPrefix}-city`}
          label="City"
          col="col-md-5"
          required={required}
          disabled={copied}
          value={address.city}
          onChange={update('city')}
          autoComplete="off"
        />
        <SelectInput
          id={`${idPrefix}-state`}
          label="State"
          col="col-md-3"
          required={required}
          options={US_STATES}
          placeholder="—"
          disabled={copied}
          value={address.state}
          onChange={update('state')}
        />
        <TextInput
          id={`${idPrefix}-zip`}
          label="ZIP Code"
          col="col-md-4"
          required={required}
          maxLength={10}
          disabled={copied}
          value={address.zip}
          onChange={update('zip')}
          autoComplete="off"
        />
      </div>
    </div>
  );
}
